import { memo, PropsWithChildren, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { ListItemVariant } from '@xemplo/stateful-list-core';

import { useSingleSelect } from '../../utils/hooks/use-single-select';
import { ListItemProps } from '../../utils/types/stateful-list.types';
import * as S from '../common/common.styles';

import { SelectedTick } from './list-item.styles';

export const ListItemSingle = memo(
  ({ children, className, item }: PropsWithChildren<ListItemProps>) => {
    const {
      activeDescendantId,
      isNoResult,
      isSelected: getIsSelected,
      toggleItem,
      autoScrollToSelected,
    } = useSingleSelect();
    const { focusId, value, icon: Icon, variant } = item;
    const isDestructive = variant === ListItemVariant.Destructive;

    const elementRef = useRef<HTMLLIElement>(null);
    const selected = getIsSelected(value);

    useEffect(() => {
      if (autoScrollToSelected && selected) {
        const element = elementRef.current;
        element?.scrollIntoView({ block: 'center', behavior: 'auto' });
      }
      // We only want to run this on mount
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <S.ListItem
        ref={elementRef}
        id={focusId}
        className={classNames(
          { selected, 'no-result': isNoResult, destructive: isDestructive },
          className,
        )}
        // Keeps focus on whatever opened the list. Without this, mousedown focuses the
        // item before the click lands, blurring a searchable dropdown's input while its
        // text is still blank — which drops the floating label until the click restores
        // it. Keyboard navigation focuses items explicitly, so it is unaffected.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => toggleItem(value)}
        tabIndex={activeDescendantId === focusId ? 0 : -1}
      >
        {Icon && <Icon />}
        {children}
        {selected && !isDestructive && <SelectedTick />}
      </S.ListItem>
    );
  },
);

export default ListItemSingle;
